import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Fade,
  TextField,
} from "@mui/material";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SummarizeIcon from "@mui/icons-material/Summarize";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HistoryIcon from "@mui/icons-material/History";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { protectedResources } from "../../authConfig";

const API_BASE = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/sal-committee`;
const BRAND = "#0563B0";
const BRAND_DARK = "#0454a0";

const RUN_TYPE_LABELS = {
  phase_a: "עיבוד הגשות",
  phase_b: "עדכון סיכום",
  competitors: "מתחרים",
};

const RUN_TYPE_COLORS = {
  phase_a: { bg: "#ebf7ff", color: BRAND },
  phase_b: { bg: "#f0faf4", color: "#217346" },
  competitors: { bg: "#fdf2e9", color: "#c2681d" },
};

const triggerBrowserDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Small step header used at the top of each tab panel
const SectionIntro = ({ icon, title, description, accent = "#ebf7ff" }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75, mb: 3 }}>
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent} 0%, #ffffff 100%)`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight="bold" color="#1f2937">
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="#6b7280" sx={{ mt: 0.3 }}>
          {description}
        </Typography>
      )}
    </Box>
  </Box>
);

const FileChooserButton = ({ file, placeholder, accept, onChange, icon }) => (
  <Button
    variant="outlined"
    component="label"
    size="small"
    startIcon={icon}
    sx={{
      borderColor: file ? "#217346" : "rgba(0,0,0,0.15)",
      color: file ? "#217346" : "#374151",
      bgcolor: file ? "#f0faf4" : "#fff",
      borderRadius: 2.5,
      textTransform: "none",
      fontWeight: 600,
      transition: "box-shadow 0.15s, background-color 0.15s",
      "&:hover": {
        bgcolor: file ? "#e6f7ec" : "#f8fafc",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      },
    }}
  >
    {file ? file.name : placeholder}
    <input type="file" hidden accept={accept} onChange={onChange} />
  </Button>
);

const SalCommitteePage = () => {
  const { instance, accounts } = useMsal();

  const [tab, setTab] = useState(0);

  const [phaseAFile, setPhaseAFile] = useState(null);
  const [phaseAState, setPhaseAState] = useState({ loading: false, error: null });
  const [phaseBSourceMode, setPhaseBSourceMode] = useState("latest"); // "latest" | "pick" | "upload"
  const [phaseBSourceFile, setPhaseBSourceFile] = useState(null);
  const [phaseBSourceRunId, setPhaseBSourceRunId] = useState("");
  const [phaseBState, setPhaseBState] = useState({ loading: false, error: null, message: null });
  const [rebuildState, setRebuildState] = useState({ loading: false, error: null, message: null });
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [referenceFile, setReferenceFile] = useState(null);
  const [summaryFile, setSummaryFile] = useState(null);
  const [competitorsState, setCompetitorsState] = useState({ loading: false, error: null });
  const [history, setHistory] = useState({ loading: false, items: [], error: null });
  const [historyFilterType, setHistoryFilterType] = useState("all");
  const [historySortOrder, setHistorySortOrder] = useState("newest");
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);
  const [deleteHistoryState, setDeleteHistoryState] = useState({ loading: false });
  const [scheduleDates, setScheduleDates] = useState({ dateFrom: "", dateTo: "" });
  const [scheduleState, setScheduleState] = useState({ loading: false, error: null, message: null });

  const getAccessToken = useCallback(async () => {
    const account = accounts[0];
    const request = { scopes: protectedResources.apiScopes.scopes.read, account };
    try {
      const result = await instance.acquireTokenSilent(request);
      return result.accessToken;
    } catch (silentError) {
      // Silent (hidden-iframe) renewal can fail for reasons that aren't about
      // the token itself — third-party cookies blocked, session expired,
      // monitor_window_timeout — same fallback useFetchWithMsal.js already
      // uses elsewhere in this app: fall back to an interactive redirect
      // rather than surfacing a raw MSAL error to the user.
      if (
        silentError instanceof InteractionRequiredAuthError ||
        silentError?.errorCode === "monitor_window_timeout"
      ) {
        await instance.acquireTokenRedirect(request);
        return null; // page is navigating away; caller's request won't complete this cycle
      }
      throw silentError;
    }
  }, [accounts, instance]);

  // Handles a response that may be EITHER a downloadable .xlsx file OR a
  // plain JSON status object (e.g. Phase B when there was nothing new to
  // process) — the backend proxy relays either as-is; branch on the real
  // Content-Type rather than assuming one shape.
  const handleFileOrJsonResponse = async (response, downloadFilename) => {
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try {
        message = JSON.parse(text).message || text;
      } catch {
        // keep raw text
      }
      throw new Error(message || `שגיאה (${response.status})`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("spreadsheetml")) {
      const blob = await response.blob();
      triggerBrowserDownload(blob, downloadFilename);
      return { downloaded: true };
    }
    return response.json();
  };

  const loadHistory = useCallback(async () => {
    setHistory((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בטעינת היסטוריה (${response.status})`);
      const items = await response.json();
      setHistory({ loading: false, items, error: null });
    } catch (err) {
      setHistory({ loading: false, items: [], error: err.message || "שגיאה בטעינת היסטוריה" });
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadScheduleSettings = useCallback(async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בטעינת הגדרות הסריקה האוטומטית (${response.status})`);
      const settings = await response.json();
      setScheduleDates({ dateFrom: settings.date_from || "", dateTo: settings.date_to || "" });
    } catch (err) {
      setScheduleState((prev) => ({ ...prev, error: err.message || "שגיאה בטעינת הגדרות הסריקה האוטומטית" }));
    }
  }, [getAccessToken]);

  useEffect(() => {
    loadScheduleSettings();
  }, [loadScheduleSettings]);

  const runPhaseA = async () => {
    if (!phaseAFile) {
      setPhaseAState({ loading: false, error: "יש לבחור קובץ PDF תחילה" });
      return;
    }
    setPhaseAState({ loading: true, error: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("pdf_file", phaseAFile);

      const response = await fetch(`${API_BASE}/phase-a/process`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      await handleFileOrJsonResponse(response, "הגשות_לסל.xlsx");
      setPhaseAState({ loading: false, error: null });
      loadHistory();
    } catch (err) {
      setPhaseAState({ loading: false, error: err.message || "שגיאה בעיבוד קובץ ההגשות" });
    }
  };

  const runPhaseB = async () => {
    if (phaseBSourceMode === "upload" && !phaseBSourceFile) {
      setPhaseBState({ loading: false, error: "יש לבחור קובץ להעלאה", message: null });
      return;
    }
    if (phaseBSourceMode === "pick" && !phaseBSourceRunId) {
      setPhaseBState({ loading: false, error: "יש לבחור הרצה מההיסטוריה", message: null });
      return;
    }
    setPhaseBState({ loading: true, error: null, message: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      if (phaseBSourceMode === "upload" && phaseBSourceFile) {
        formData.append("phase_a_file", phaseBSourceFile);
      } else if (phaseBSourceMode === "pick" && phaseBSourceRunId) {
        formData.append("phase_a_run_id", phaseBSourceRunId);
      }

      const response = await fetch(`${API_BASE}/phase-b/run`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await handleFileOrJsonResponse(response, "ועדת_סל_סיכום.xlsx");
      const message = result?.downloaded
        ? null
        : result?.message || (result?.status === "no_pending" ? "אין פרוטוקולים חדשים לעיבוד" : null);
      setPhaseBState({ loading: false, error: null, message });
      loadHistory();
    } catch (err) {
      setPhaseBState({ loading: false, error: err.message || "שגיאה בעדכון הסיכום", message: null });
    }
  };

  const runRebuild = async () => {
    setRebuildConfirmOpen(false);
    setRebuildState({ loading: true, error: null, message: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      if (phaseBSourceMode === "upload" && phaseBSourceFile) {
        formData.append("phase_a_file", phaseBSourceFile);
      } else if (phaseBSourceMode === "pick" && phaseBSourceRunId) {
        formData.append("phase_a_run_id", phaseBSourceRunId);
      }

      const response = await fetch(`${API_BASE}/phase-b/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const result = await handleFileOrJsonResponse(response, "ועדת_סל_סיכום.xlsx");
      const message = result?.downloaded
        ? null
        : result?.message || (result?.status === "no_pending" ? "אין פרוטוקולים לעיבוד" : null);
      setRebuildState({ loading: false, error: null, message });
      loadHistory();
    } catch (err) {
      setRebuildState({ loading: false, error: err.message || "שגיאה בבנייה מחדש", message: null });
    }
  };

  const saveScheduleSettings = async () => {
    if (!scheduleDates.dateFrom || !scheduleDates.dateTo) {
      setScheduleState({ loading: false, error: "יש להגדיר תאריך התחלה וסיום", message: null });
      return;
    }
    setScheduleState({ loading: true, error: null, message: null });
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/settings`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ date_from: scheduleDates.dateFrom, date_to: scheduleDates.dateTo }),
      });
      if (!response.ok) throw new Error(`שגיאה בשמירת הגדרות הסריקה האוטומטית (${response.status})`);
      setScheduleState({ loading: false, error: null, message: "הטווח נשמר בהצלחה" });
    } catch (err) {
      setScheduleState({ loading: false, error: err.message || "שגיאה בשמירת הגדרות הסריקה האוטומטית", message: null });
    }
  };

  const runFindCompetitors = async () => {
    if (!referenceFile || !summaryFile) {
      setCompetitorsState({ loading: false, error: "יש לבחור גם את טבלת הייחוס וגם את קובץ הסיכום" });
      return;
    }
    setCompetitorsState({ loading: true, error: null });
    try {
      const token = await getAccessToken();
      const formData = new FormData();
      formData.append("reference_file", referenceFile);
      formData.append("summary_file", summaryFile);

      const response = await fetch(`${API_BASE}/competitors/find`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      await handleFileOrJsonResponse(response, "מתחרים.xlsx");
      setCompetitorsState({ loading: false, error: null });
      loadHistory();
    } catch (err) {
      setCompetitorsState({ loading: false, error: err.message || "שגיאה באיתור מתחרים" });
    }
  };

  const setAsDefaultRun = async (item) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history/${item._id}/set-default`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בסימון ברירת מחדל (${response.status})`);
      loadHistory();
    } catch (err) {
      setHistory((prev) => ({ ...prev, error: err.message || "שגיאה בסימון ברירת מחדל" }));
    }
  };

  const downloadHistoryItem = async (item) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history/${item._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה בהורדה (${response.status})`);
      const blob = await response.blob();
      triggerBrowserDownload(blob, item.filename || "קובץ.xlsx");
    } catch (err) {
      setHistory((prev) => ({ ...prev, error: err.message || "שגיאה בהורדת קובץ" }));
    }
  };

  const deleteHistoryRun = async () => {
    if (!pendingDeleteItem) return;
    setDeleteHistoryState({ loading: true });
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE}/history/${pendingDeleteItem._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`שגיאה במחיקת קובץ (${response.status})`);
      setPendingDeleteItem(null);
      setDeleteHistoryState({ loading: false });
      loadHistory();
    } catch (err) {
      setDeleteHistoryState({ loading: false });
      setHistory((prev) => ({ ...prev, error: err.message || "שגיאה במחיקת קובץ" }));
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    try {
      return new Date(isoString).toLocaleString("he-IL");
    } catch {
      return isoString;
    }
  };

  const phaseASourceCount = history.items.filter((item) => item.run_type === "phase_a").length;

  const displayedHistoryItems = useMemo(() => {
    const filtered =
      historyFilterType === "all"
        ? history.items
        : history.items.filter((item) => item.run_type === historyFilterType);
    const sorted = [...filtered].sort((a, b) => {
      const diff = new Date(a.created_at) - new Date(b.created_at);
      return historySortOrder === "newest" ? -diff : diff;
    });
    return sorted;
  }, [history.items, historyFilterType, historySortOrder]);

  const rebuildDisabled =
    rebuildState.loading ||
    (phaseBSourceMode === "upload" && !phaseBSourceFile) ||
    (phaseBSourceMode === "pick" && !phaseBSourceRunId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pt: { xs: 10, sm: 11 }, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2, sm: 3 },
            mb: 0,
            background: "linear-gradient(135deg, #ffffff 0%, #f4f9ff 100%)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderBottom: "none",
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 1.75,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(5,99,176,0.14) 0%, rgba(5,99,176,0.05) 100%)",
              boxShadow: "0 2px 8px rgba(5,99,176,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalPharmacyIcon sx={{ color: BRAND, fontSize: 32 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#1f2937">
              שליפת עיקרי החלטות מועדת סל הבריאות
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          border: "1px solid rgba(0,0,0,0.08)",
          borderTop: "none",
          borderBottom: "none",
          bgcolor: "#fafbfc",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              minHeight: 52,
              borderRadius: "10px 10px 0 0",
              transition: "background-color 0.15s",
              "&:hover": { bgcolor: "rgba(5,99,176,0.05)" },
            },
            "& .Mui-selected": { color: BRAND },
            "& .MuiTabs-indicator": { bgcolor: BRAND, height: 3, borderRadius: "3px 3px 0 0" },
          }}
        >
          <Tab icon={<CloudUploadIcon fontSize="small" />} iconPosition="start" label="עיבוד קובץ הגשות" />
          <Tab icon={<SummarizeIcon fontSize="small" />} iconPosition="start" label="עדכון סיכום מצטבר" />
          <Tab icon={<PersonSearchIcon fontSize="small" />} iconPosition="start" label="איתור מתחרים" />
          <Tab
            icon={<HistoryIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                היסטוריית קבצים
                {history.items.length > 0 && (
                  <Chip
                    label={history.items.length}
                    size="small"
                    sx={{ height: 18, fontSize: "0.68rem", bgcolor: "#ebf7ff", color: BRAND, fontWeight: 700 }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>
      </Paper>

      {/* Tab content */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          borderBottomLeftRadius: 14,
          borderBottomRightRadius: 14,
          border: "1px solid rgba(0,0,0,0.08)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          p: { xs: 2.5, md: 3.5 },
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
          minHeight: 320,
        }}
      >
        {/* Tab 0 — Phase A */}
        {tab === 0 && (
          <Box>
            <SectionIntro
              icon={<CloudUploadIcon sx={{ color: BRAND }} />}
              title="עיבוד PDF הגשות"
              accent="#ebf7ff"
            />

            <Box
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid rgba(0,0,0,0.06)",
                borderInlineStart: `4px solid ${BRAND}`,
                borderRadius: 3,
                p: 2.5,
                mb: 3,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="body2" fontWeight={700} color="#1f2937" sx={{ mb: 0.5 }}>
                קובץ PDF של הגשות לסל הבריאות
              </Typography>
              <Typography variant="caption" color="#6b7280" sx={{ display: "block", mb: 1.5 }}>
                קובץ "עיקרי החלטות הוועדה" שהתקבל מוועדת סל הבריאות
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <FileChooserButton
                  file={phaseAFile}
                  placeholder="בחר קובץ PDF"
                  accept="application/pdf"
                  icon={<PictureAsPdfIcon fontSize="small" />}
                  onChange={(e) => setPhaseAFile(e.target.files?.[0] || null)}
                />
                {phaseAFile && (
                  <Chip
                    label={`${(phaseAFile.size / 1024).toFixed(0)} KB`}
                    size="small"
                    sx={{ bgcolor: "#ebf7ff", color: BRAND, fontWeight: 600, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={runPhaseA}
              disabled={phaseAState.loading || !phaseAFile}
              startIcon={phaseAState.loading ? null : <CloudUploadIcon />}
              sx={{
                bgcolor: BRAND,
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: "0 2px 8px rgba(5,99,176,0.25)",
                "&:hover": { bgcolor: BRAND_DARK, boxShadow: "0 4px 12px rgba(5,99,176,0.3)" },
              }}
            >
              {phaseAState.loading ? <CircularProgress size={20} color="inherit" /> : "עבד קובץ הגשות והורד"}
            </Button>
            {phaseAState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseAState.error}
              </Alert>
            )}
          </Box>
        )}

        {/* Tab 1 — Phase B */}
        {tab === 1 && (
          <Box>
            <SectionIntro
              icon={<SummarizeIcon sx={{ color: "#217346" }} />}
              title="עדכון סיכום מצטבר"
              accent="#f0faf4"
            />

            <Box
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid rgba(0,0,0,0.06)",
                borderInlineStart: "4px solid #217346",
                borderRadius: 3,
                p: 2.5,
                mb: 3,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="body2" fontWeight={700} color="#1f2937" sx={{ mb: 1.5 }}>
                מקור נתוני עיבוד ההגשות
              </Typography>
              <RadioGroup
                row
                value={phaseBSourceMode}
                onChange={(e) => setPhaseBSourceMode(e.target.value)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value="latest"
                  control={<Radio size="small" sx={{ color: "#217346", "&.Mui-checked": { color: "#217346" } }} />}
                  label="ברירת מחדל"
                />
                <FormControlLabel
                  value="pick"
                  control={<Radio size="small" sx={{ color: "#217346", "&.Mui-checked": { color: "#217346" } }} />}
                  label="בחר מההיסטוריה"
                />
                <FormControlLabel
                  value="upload"
                  control={<Radio size="small" sx={{ color: "#217346", "&.Mui-checked": { color: "#217346" } }} />}
                  label="העלה קובץ"
                />
              </RadioGroup>
              {phaseBSourceMode === "latest" && (
                <Typography variant="caption" color="#6b7280" sx={{ display: "block", mt: 0.75 }}>
                  קובץ מסומן בכוכב, אחרת ההרצה האחרונה
                </Typography>
              )}

              {phaseBSourceMode === "pick" && (
                <Box sx={{ mt: 2 }}>
                  {phaseASourceCount === 0 ? (
                    <Typography variant="body2" color="#9ca3af">
                      אין עדיין הרצות עיבוד הגשות בהיסטוריה — הרץ עיבוד הגשות תחילה
                    </Typography>
                  ) : (
                    <Select
                      size="small"
                      value={phaseBSourceRunId}
                      onChange={(e) => setPhaseBSourceRunId(e.target.value)}
                      displayEmpty
                      sx={{ minWidth: 320, bgcolor: "#fff", borderRadius: 2 }}
                    >
                      <MenuItem value="" disabled>
                        בחר הרצה...
                      </MenuItem>
                      {history.items
                        .filter((item) => item.run_type === "phase_a")
                        .map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {formatDate(item.created_at)} — {item.username}
                            {item.is_default ? " ⭐" : ""}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                </Box>
              )}

              {phaseBSourceMode === "upload" && (
                <Box sx={{ mt: 2 }}>
                  <FileChooserButton
                    file={phaseBSourceFile}
                    placeholder="בחר קובץ עיבוד הגשות (Excel)"
                    accept=".xlsx,.xls"
                    icon={<DescriptionIcon fontSize="small" />}
                    onChange={(e) => setPhaseBSourceFile(e.target.files?.[0] || null)}
                  />
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              onClick={runPhaseB}
              disabled={
                phaseBState.loading ||
                (phaseBSourceMode === "upload" && !phaseBSourceFile) ||
                (phaseBSourceMode === "pick" && !phaseBSourceRunId)
              }
              startIcon={phaseBState.loading ? null : <SummarizeIcon />}
              sx={{
                bgcolor: "#217346",
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: "0 2px 8px rgba(33,115,70,0.25)",
                "&:hover": { bgcolor: "#1a5c38", boxShadow: "0 4px 12px rgba(33,115,70,0.3)" },
              }}
            >
              {phaseBState.loading ? <CircularProgress size={20} color="inherit" /> : "עדכן סיכום והורד קובץ"}
            </Button>
            {phaseBState.message && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseBState.message}
              </Alert>
            )}
            {phaseBState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {phaseBState.error}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                bgcolor: "#f8fafc",
                border: "1px solid rgba(0,0,0,0.06)",
                borderInlineStart: "4px solid #217346",
                borderRadius: 3,
                p: 2.5,
                mb: 3,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <EventRepeatIcon fontSize="small" sx={{ color: "#217346" }} />
                <Typography variant="body2" fontWeight={700} color="#1f2937">
                  סריקה אוטומטית יומית
                </Typography>
              </Box>
              <Typography variant="body2" color="#6b7280" sx={{ mb: 2 }}>
                בטווח התאריכים, המערכת תבדוק כל יום אם יש מסמכים חדשים ותשלח עדכון במייל.
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  label="מתאריך"
                  InputLabelProps={{ shrink: true }}
                  value={scheduleDates.dateFrom}
                  onChange={(e) => setScheduleDates((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  sx={{ bgcolor: "#fff" }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="עד תאריך"
                  InputLabelProps={{ shrink: true }}
                  value={scheduleDates.dateTo}
                  onChange={(e) => setScheduleDates((prev) => ({ ...prev, dateTo: e.target.value }))}
                  sx={{ bgcolor: "#fff" }}
                />
                <Button
                  variant="contained"
                  onClick={saveScheduleSettings}
                  disabled={scheduleState.loading}
                  sx={{
                    bgcolor: "#217346",
                    borderRadius: 2.5,
                    textTransform: "none",
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(33,115,70,0.25)",
                    "&:hover": { bgcolor: "#1a5c38", boxShadow: "0 4px 12px rgba(33,115,70,0.3)" },
                  }}
                >
                  {scheduleState.loading ? <CircularProgress size={20} color="inherit" /> : "שמור טווח"}
                </Button>
              </Box>
              {scheduleState.message && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  {scheduleState.message}
                </Alert>
              )}
              {scheduleState.error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {scheduleState.error}
                </Alert>
              )}
            </Box>

            <Box
              sx={{
                bgcolor: "#fdf7ed",
                border: "1px solid #f5e2bd",
                borderInlineStart: "4px solid #c2681d",
                borderRadius: 3,
                p: 2.5,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <Typography variant="body2" fontWeight={700} color="#92661a" sx={{ mb: 2 }}>
                סרוק מחדש (ללא תלות בסריקות קודמות)
              </Typography>
              <Tooltip title="סרוק מחדש (ללא תלות בסריקות קודמות)">
                <span>
                  <IconButton
                    color="warning"
                    onClick={() => setRebuildConfirmOpen(true)}
                    disabled={rebuildDisabled}
                    sx={{ border: "1px solid", borderColor: "warning.main" }}
                  >
                    {rebuildState.loading ? <CircularProgress size={20} color="inherit" /> : <RestartAltIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              {rebuildState.message && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  {rebuildState.message}
                </Alert>
              )}
              {rebuildState.error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {rebuildState.error}
                </Alert>
              )}
            </Box>

            <Dialog open={rebuildConfirmOpen} onClose={() => setRebuildConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
              <DialogTitle>סרוק מחדש — לאשר?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  הפעולה תמחק את קובץ הסיכום הנוכחי (עם גיבוי אוטומטי) ותבנה אותו מחדש מכל הפרוטוקולים.
                  זה עלול לקחת כמה דקות. להמשיך?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRebuildConfirmOpen(false)} sx={{ textTransform: "none" }}>
                  ביטול
                </Button>
                <Button onClick={runRebuild} color="warning" variant="contained" sx={{ textTransform: "none", fontWeight: 600 }}>
                  כן, בנה מחדש
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Tab 2 — Competitors */}
        {tab === 2 && (
          <Box>
            <SectionIntro
              icon={<PersonSearchIcon sx={{ color: "#c2681d" }} />}
              title="איתור מתחרים"
              description="משווה בין קובץ הסיכום לטבלת הייחוס ומוריד קובץ Excel עם המתחרים שנמצאו. להריץ אחרי עיבוד ההגשות ועדכון הסיכום."
              accent="#fdf2e9"
            />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mb: 3,
                bgcolor: "#fdf7ed",
                border: "1px solid #f5e2bd",
                borderInlineStart: "4px solid #c2681d",
                borderRadius: 3,
                p: 2.5,
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 0.75 }}>
                  טבלת מילות חיפוש למתחרים
                </Typography>
                <FileChooserButton
                  file={referenceFile}
                  placeholder="בחר קובץ Excel"
                  accept=".xlsx,.xls"
                  icon={<InsertDriveFileIcon fontSize="small" />}
                  onChange={(e) => setReferenceFile(e.target.files?.[0] || null)}
                />
              </Box>

              <Box>
                <Typography variant="body2" fontWeight={600} color="#374151" sx={{ mb: 0.75 }}>
                  קובץ הסיכום המצטבר
                </Typography>
                <FileChooserButton
                  file={summaryFile}
                  placeholder="בחר קובץ Excel"
                  accept=".xlsx,.xls"
                  icon={<InsertDriveFileIcon fontSize="small" />}
                  onChange={(e) => setSummaryFile(e.target.files?.[0] || null)}
                />
              </Box>
            </Box>

            <Divider sx={{ mb: 2.5 }} />

            <Button
              variant="contained"
              onClick={runFindCompetitors}
              disabled={competitorsState.loading || !referenceFile || !summaryFile}
              startIcon={competitorsState.loading ? null : <PersonSearchIcon />}
              sx={{
                bgcolor: "#c2681d",
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                boxShadow: "0 2px 8px rgba(194,104,29,0.25)",
                "&:hover": { bgcolor: "#a35619", boxShadow: "0 4px 12px rgba(194,104,29,0.3)" },
              }}
            >
              {competitorsState.loading ? <CircularProgress size={20} color="inherit" /> : "אתר מתחרים והורד קובץ"}
            </Button>
            {competitorsState.error && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {competitorsState.error}
              </Alert>
            )}
          </Box>
        )}

        {/* Tab 3 — History */}
        {tab === 3 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <SectionIntro
                icon={<HistoryIcon sx={{ color: BRAND }} />}
                title="קבצים אחרונים"
                accent="#ebf7ff"
              />
              <Tooltip title="רענן">
                <span>
                  <IconButton
                    onClick={loadHistory}
                    disabled={history.loading}
                    size="small"
                    sx={{
                      border: "1px solid rgba(0,0,0,0.1)",
                      color: BRAND,
                      "&:hover": { bgcolor: "rgba(5,99,176,0.08)" },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {history.error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {history.error}
              </Alert>
            )}

            {history.items.length > 0 && (
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <Select
                  size="small"
                  value={historyFilterType}
                  onChange={(e) => setHistoryFilterType(e.target.value)}
                  sx={{ minWidth: 160, bgcolor: "#fff", borderRadius: 2 }}
                >
                  <MenuItem value="all">כל הסוגים</MenuItem>
                  <MenuItem value="phase_a">{RUN_TYPE_LABELS.phase_a}</MenuItem>
                  <MenuItem value="phase_b">{RUN_TYPE_LABELS.phase_b}</MenuItem>
                  <MenuItem value="competitors">{RUN_TYPE_LABELS.competitors}</MenuItem>
                </Select>
                <Select
                  size="small"
                  value={historySortOrder}
                  onChange={(e) => setHistorySortOrder(e.target.value)}
                  sx={{ minWidth: 160, bgcolor: "#fff", borderRadius: 2 }}
                >
                  <MenuItem value="newest">מהחדש לישן</MenuItem>
                  <MenuItem value="oldest">מהישן לחדש</MenuItem>
                </Select>
              </Box>
            )}

            {history.loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={28} />
              </Box>
            ) : history.items.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 6,
                  color: "#9ca3af",
                  background: "linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%)",
                  borderRadius: 3,
                  border: "1px dashed rgba(0,0,0,0.12)",
                }}
              >
                <HistoryIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">אין הרצות עדיין</Typography>
              </Box>
            ) : displayedHistoryItems.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "#9ca3af",
                  bgcolor: "#f8fafc",
                  borderRadius: 3,
                  border: "1px dashed rgba(0,0,0,0.12)",
                }}
              >
                <Typography variant="body2">אין תוצאות עבור הסינון שנבחר</Typography>
              </Box>
            ) : (
              <TableContainer
                sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: 3, boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        background: "linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%)",
                        "& .MuiTableCell-root": { borderBottom: `2px solid ${BRAND}22` },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>סוג</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>תאריך</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>משתמש</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>קובץ</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>
                        ברירת מחדל
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>
                        הורדה
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#374151", fontSize: "0.78rem" }}>
                        מחיקה
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedHistoryItems.map((item) => {
                      const colors = RUN_TYPE_COLORS[item.run_type] || { bg: "#f1f5f9", color: "#475569" };
                      return (
                        <TableRow
                          key={item._id}
                          hover
                          sx={{
                            borderInlineStart: `3px solid ${colors.color}`,
                            transition: "background-color 0.15s",
                            "&:last-child .MuiTableCell-root": { borderBottom: "none" },
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={RUN_TYPE_LABELS[item.run_type] || item.run_type}
                              size="small"
                              sx={{ bgcolor: colors.bg, color: colors.color, fontWeight: 700, fontSize: "0.72rem" }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: "#6b7280", fontSize: "0.85rem" }}>{formatDate(item.created_at)}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{item.username}</TableCell>
                          <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#6b7280" }} title={item.filename}>
                            {item.filename}
                          </TableCell>
                          <TableCell align="center">
                            {item.run_type === "phase_a" && (
                              <Tooltip title={item.is_default ? "ברירת המחדל הנוכחית" : "סמן כברירת מחדל"}>
                                <IconButton
                                  size="small"
                                  onClick={() => setAsDefaultRun(item)}
                                  sx={{ "&:hover": { bgcolor: "rgba(245,166,35,0.1)" } }}
                                >
                                  {item.is_default ? (
                                    <StarIcon fontSize="small" sx={{ color: "#f5a623" }} />
                                  ) : (
                                    <StarBorderIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="הורדה">
                              <IconButton
                                size="small"
                                onClick={() => downloadHistoryItem(item)}
                                sx={{ color: BRAND, "&:hover": { bgcolor: "rgba(5,99,176,0.1)" } }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="מחיקה">
                              <IconButton
                                size="small"
                                onClick={() => setPendingDeleteItem(item)}
                                sx={{ color: "#c2681d", "&:hover": { bgcolor: "rgba(194,104,29,0.1)" } }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Dialog open={!!pendingDeleteItem} onClose={() => setPendingDeleteItem(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
              <DialogTitle>מחיקת קובץ מההיסטוריה — לאשר?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  הפעולה תמחק לצמיתות את הרשומה "{pendingDeleteItem?.filename}" מההיסטוריה, כולל הקובץ המאוחסן.
                  לא ניתן לשחזר. להמשיך?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPendingDeleteItem(null)} sx={{ textTransform: "none" }}>
                  ביטול
                </Button>
                <Button
                  onClick={deleteHistoryRun}
                  color="error"
                  variant="contained"
                  disabled={deleteHistoryState.loading}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {deleteHistoryState.loading ? <CircularProgress size={20} color="inherit" /> : "כן, מחק"}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SalCommitteePage;
